import React, { useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { ArrowLeft, AlertTriangle } from 'lucide-react';
import { useSales } from '../hooks/useSales';
import { localDateStr } from '../utils/customers';
import { fmtDate, money } from '../utils/quotes';
import { PAYMENT_METHODS, PAYMENT_METHOD_LABELS, balanceOf, checkPayment, isOpenSale } from '../utils/sales';
import { LinesTable, inputCls, labelCls } from '../components/DocumentLines';
import { SaleBadge } from './SalesPage';

const btn = 'text-xs font-bold px-3 py-2 rounded-xl flex items-center gap-1.5 disabled:opacity-50';
const btnMain = `${btn} bg-seryi-green text-seryi-bg-dark font-extrabold`;
const btnDanger = `${btn} border border-red-500/40 text-red-300 hover:bg-red-500/10`;

// Pide un motivo antes de anular (la base lo exige y queda en la bitácora).
function ReasonDialog({ title, help, confirmLabel, onConfirm, onClose }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label={title}>
      <div className="bg-seryi-bg border border-seryi-bg-light rounded-3xl p-6 w-full max-w-md space-y-4">
        <h3 className="font-bold text-sm text-seryi-heading">{title}</h3>
        <p className="text-[11px] text-seryi-text">{help}</p>
        <div className="space-y-1">
          <label htmlFor="void-reason" className={labelCls}>Motivo</label>
          <textarea id="void-reason" rows={3} className={inputCls} value={reason} onChange={(e) => setReason(e.target.value)} />
        </div>
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="text-xs font-bold text-seryi-text px-3 py-2">Cancelar</button>
          <button type="button" disabled={busy || !reason.trim()} onClick={async () => { setBusy(true); const ok = await onConfirm(reason.trim()); setBusy(false); if (ok) onClose(); }} className={btnDanger}>{busy ? 'Anulando…' : confirmLabel}</button>
        </div>
      </div>
    </div>
  );
}

export default function SaleDetailPage() {
  const { id } = useParams();
  const location = useLocation();
  const { customers, settings, loading, tableMissing, getSale, registerPayment, voidPayment, voidSale } = useSales({ withList: false });
  const [data, setData] = useState(null);
  const [opening, setOpening] = useState(true);
  const [busy, setBusy] = useState(false);
  const [dialog, setDialog] = useState(null); // { kind: 'sale' } | { kind: 'payment', payment }
  const [form, setForm] = useState({ amount: '', method: 'efectivo', reference: '', paid_at: localDateStr() });
  const warnings = location.state?.warnings || [];

  useEffect(() => {
    let alive = true;
    setOpening(true);
    getSale(id).then((d) => {
      if (!alive) return;
      setData(d);
      if (d) setForm((f) => ({ ...f, amount: balanceOf(d.sale) > 0 ? String(balanceOf(d.sale)) : '' }));
      setOpening(false);
    });
    return () => { alive = false; };
  }, [id, getSale]);

  const refresh = async () => {
    const d = await getSale(id);
    if (d) { setData(d); setForm((f) => ({ ...f, amount: balanceOf(d.sale) > 0 ? String(balanceOf(d.sale)) : '', reference: '' })); }
    return d;
  };

  if (tableMissing) return <div className="p-6 text-xs text-amber-300">Falta ejecutar la migración <code>20261003c_sales.sql</code> en Supabase para usar las ventas.</div>;
  if (opening || loading) return <p className="text-xs text-seryi-text">Cargando…</p>;
  if (!data) return <div className="space-y-3 text-xs text-seryi-text"><p>No se encontró la venta.</p><Link to="/v2/ventas" className="text-seryi-green font-bold">Volver a la lista</Link></div>;

  const { sale, items, payments, movements, quote } = data;
  const customer = customers.find((c) => c.id === sale.customer_id) || null;
  const balance = balanceOf(sale);
  const voided = sale.status === 'anulada';
  const activePayments = payments.filter((p) => !p.voided_at);
  const problem = form.amount === '' ? null : checkPayment(sale, form.amount);

  const pay = async () => {
    setBusy(true);
    const row = await registerPayment(sale.id, { amount: form.amount, method: form.method, reference: form.reference, paid_at: form.paid_at ? `${form.paid_at}T12:00:00` : null });
    if (row) await refresh();
    setBusy(false);
  };

  return (
    <div className="space-y-5 max-w-5xl">
      <Link to="/v2/ventas" className="text-[11px] text-seryi-text hover:text-seryi-heading flex items-center gap-1"><ArrowLeft size={12} /> Ventas</Link>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-extrabold text-seryi-heading flex items-center gap-2 flex-wrap">Venta {sale.number} <SaleBadge sale={sale} /></h2>
          <p className="text-[11px] text-seryi-text mt-1">
            Creada {fmtDate(sale.created_at)}{sale.due_date ? ` · vence ${fmtDate(sale.due_date)}` : ''}
            {quote && <> · de la cotización <Link className="text-seryi-green font-bold" to={`/v2/cotizaciones/${quote.id}`}>{quote.number}{quote.version > 1 ? ` v${quote.version}` : ''}</Link></>}
          </p>
          <p className="text-xs text-seryi-heading mt-1">{customer ? (customer.legal_name || customer.name) : 'Sin cliente'}{customer?.tax_id ? ` · NIT/CC ${customer.tax_id}` : ''}</p>
        </div>
        {!voided && <button type="button" onClick={() => setDialog({ kind: 'sale' })} className={btnDanger}>Anular venta</button>}
      </div>

      {warnings.length > 0 && (
        <div className="p-3 rounded-xl border border-amber-500/40 bg-amber-500/10 text-xs text-amber-200 space-y-0.5">
          {warnings.map((w) => <p key={w.product_id}><AlertTriangle size={12} className="inline mr-1" />{w.name}: las existencias quedaron en {w.stock_after}.</p>)}
        </div>
      )}
      {voided && <div className="p-3 rounded-xl border border-slate-600/50 text-xs text-seryi-text">Anulada {fmtDate(sale.voided_at)}. Motivo: {sale.void_reason}. Las existencias se devolvieron.</div>}

      <LinesTable editable={false} lines={items} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-seryi-bg border border-seryi-bg-light rounded-2xl p-4 text-xs text-seryi-heading space-y-1.5">
          <div className="flex justify-between"><span className="text-seryi-text">Subtotal</span><span>{money(sale.subtotal)}</span></div>
          <div className="flex justify-between"><span className="text-seryi-text">Descuentos</span><span>−{money(sale.discount_total)}</span></div>
          <div className="flex justify-between"><span className="text-seryi-text">Impuestos</span><span>{money(sale.tax_total)}</span></div>
          <div className="flex justify-between text-base font-black border-t border-seryi-bg-light pt-2"><span>Total ({settings.currency})</span><span>{money(sale.total)}</span></div>
          <div className="flex justify-between"><span className="text-seryi-text">Pagado</span><span className="text-emerald-400 font-bold">{money(sale.paid_total)}</span></div>
          <div className="flex justify-between text-base font-black"><span>Saldo</span><span className={balance > 0 && !voided ? 'text-amber-300' : ''}>{voided ? '—' : money(balance)}</span></div>
        </div>

        {!voided && isOpenSale(sale) && (
          <form className="bg-seryi-bg border border-seryi-bg-light rounded-2xl p-4 space-y-3" onSubmit={(e) => { e.preventDefault(); if (!problem) pay(); }}>
            <h3 className="font-bold text-sm text-seryi-heading">Registrar abono</h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><label htmlFor="pay-amount" className={labelCls}>Valor</label><input id="pay-amount" type="number" min="0" step="any" className={inputCls} value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} /></div>
              <div className="space-y-1"><label htmlFor="pay-method" className={labelCls}>Medio</label>
                <select id="pay-method" className={inputCls} value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value })}>{PAYMENT_METHODS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></div>
              <div className="space-y-1"><label htmlFor="pay-ref" className={labelCls}>Referencia</label><input id="pay-ref" className={inputCls} value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} /></div>
              <div className="space-y-1"><label htmlFor="pay-date" className={labelCls}>Fecha</label><input id="pay-date" type="date" className={inputCls} max={localDateStr()} value={form.paid_at} onChange={(e) => setForm({ ...form, paid_at: e.target.value })} /></div>
            </div>
            {problem && <p className="text-[11px] text-red-300">{problem}</p>}
            <button type="submit" disabled={busy || form.amount === '' || Boolean(problem)} className={btnMain}>{busy ? 'Registrando…' : 'Registrar abono'}</button>
          </form>
        )}
      </div>

      <div className="space-y-2">
        <h3 className="font-bold text-sm text-seryi-heading">Abonos</h3>
        {payments.length === 0 ? <p className="text-xs text-seryi-text">Todavía no hay abonos.</p> : (
          <div className="overflow-auto rounded-2xl border border-seryi-bg-light">
            <table className="w-full text-xs text-seryi-heading">
              <thead className="bg-seryi-bg-dark text-seryi-text text-[10px] uppercase"><tr><th className="p-2 text-left">Fecha</th><th className="p-2 text-left">Medio</th><th className="p-2 text-left">Referencia</th><th className="p-2 text-left">Registró</th><th className="p-2 text-right">Valor</th><th className="p-2" /></tr></thead>
              <tbody>
                {payments.map((p) => (
                  <tr key={p.id} className={`border-t border-seryi-bg-light ${p.voided_at ? 'opacity-50' : ''}`}>
                    <td className="p-2">{fmtDate(p.paid_at)}</td>
                    <td className="p-2">{PAYMENT_METHOD_LABELS[p.method] || p.method}</td>
                    <td className="p-2">{p.reference || '—'}</td>
                    <td className="p-2">{p.recorded_by || '—'}</td>
                    <td className={`p-2 text-right font-bold ${p.voided_at ? 'line-through' : 'text-emerald-400'}`}>{money(p.amount)}</td>
                    <td className="p-2 text-right whitespace-nowrap">
                      {p.voided_at ? <span className="text-[10px] text-seryi-text">Anulado: {p.void_reason}</span>
                        : !voided && <button type="button" aria-label={`Anular abono ${p.reference || money(p.amount)}`} onClick={() => setDialog({ kind: 'payment', payment: p })} className="text-[11px] text-red-300 hover:underline">Anular</button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {movements.length > 0 && (
        <div className="space-y-2">
          <h3 className="font-bold text-sm text-seryi-heading">Existencias</h3>
          <ul className="text-xs text-seryi-text space-y-0.5">
            {movements.map((m) => {
              const item = items.find((i) => i.product_id === m.product_id);
              return <li key={m.id}>{m.reason === 'venta' ? 'Descontó' : 'Devolvió'} {Math.abs(Number(m.quantity))} de {item?.description || 'un producto'} · quedaron {m.stock_after}</li>;
            })}
          </ul>
        </div>
      )}

      {dialog?.kind === 'sale' && (
        <ReasonDialog
          title={`Anular la venta ${sale.number}`}
          help={activePayments.length > 0 ? 'Esta venta tiene abonos vigentes: anúlalos primero (en la tabla de abonos). La venta anulada devuelve las existencias y no se borra.' : 'La venta queda anulada con este motivo, se devuelven las existencias y no se borra.'}
          confirmLabel="Anular venta"
          onConfirm={async (reason) => { const ok = await voidSale(sale.id, reason); if (ok) await refresh(); return ok; }}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.kind === 'payment' && (
        <ReasonDialog
          title="Anular abono"
          help={`Se anula el abono de ${money(dialog.payment.amount)}; el saldo de la venta sube. El abono no se borra: queda con el motivo.`}
          confirmLabel="Anular abono"
          onConfirm={async (reason) => { const row = await voidPayment(dialog.payment.id, reason); if (row) await refresh(); return Boolean(row); }}
          onClose={() => setDialog(null)}
        />
      )}
    </div>
  );
}
